import { createAsyncThunk } from '@reduxjs/toolkit';
import { MenuItem } from './menuSlice';
import { MenuOptionGroup } from '../MenuOption/types';
import type { RootState } from '../Store';
import { createApiRequest } from '../../utils/apiUtils';
import { v4 as uuidv4 } from 'uuid';

// メニュー一覧を取得
export const fetchMenus = createAsyncThunk<MenuItem[], string>(
  'menu/fetchMenus',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuItem[]>(`/stores/${storeId}/menus`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// メニュー追加
export const addMenu = createAsyncThunk<
  MenuItem,
  { storeId: string; name: string; price: number; imageUrl: string | null },
  { state: RootState }
>(
  'menu/addMenu',
  async ({ storeId, name, price, imageUrl }, { rejectWithValue }) => {
    try {
      const sortIndex = 0;
      return await createApiRequest<MenuItem>(`/stores/${storeId}/menus`, {
        method: 'POST',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, price, imageUrl, sortIndex }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// メニュー削除
export const deleteMenu = createAsyncThunk<
  string,
  { storeId: string; menuId: string }
>(
  'menu/deleteMenu',
  async ({ storeId, menuId }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/menus/${menuId}`, {
        method: 'DELETE',
        requiresAuth: true,
      });
      return menuId;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// メニューグループを取得
export const fetchMenuGroups = createAsyncThunk<MenuOptionGroup[], string>(
  'menu/fetchMenuGroups',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuOptionGroup[]>(`/stores/${storeId}/menu-options/groups`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// メニューグループを保存
export const saveMenuGroup = createAsyncThunk<
  void,
  { storeId: string; menuId: string; groupId: string }
>(
  'menu/saveMenuGroup',
  async ({ storeId, menuId, groupId }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/menus/${menuId}/groups/${groupId}`, {
        method: 'POST',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// メニューグループを解除
export const detachMenuGroup = createAsyncThunk<
  void,
  { storeId: string; menuId: string; menuOptionGroupId: string }
>(
  'menu/detachMenuGroup',
  async ({ storeId, menuId, menuOptionGroupId }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/menus/${menuId}/attachments/${menuOptionGroupId}`, {
        method: 'DELETE',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// 画像のリサイズ（予約画像アップロード reservationSettingsApi からも共用）
export async function resizeImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const MAX_WIDTH = 800;
      const MAX_HEIGHT = 800;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        'image/jpeg',
        0.7
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
  });
}

// S3へのアップロード
async function uploadImageToS3(resizedBlob: Blob, storeId: string, originalFileName: string): Promise<void> {
  const presignedUrlResponse = await createApiRequest<{ signedUrl: string }>(`/stores/${storeId}/menu-image/presign?filename=${encodeURIComponent(originalFileName)}&contentType=${encodeURIComponent('image/jpeg')}`, {
    method: 'GET',
    requiresAuth: true,
    headers: { 'Content-Type': 'application/json' },
  });

  if (!presignedUrlResponse || !presignedUrlResponse.signedUrl) {
    throw new Error('Failed to get presigned URL for image upload');
  }

  await fetch(presignedUrlResponse.signedUrl, {
    method: 'PUT',
    body: resizedBlob,
    headers: { 'Content-Type': 'image/jpeg' },
  });
}

// 既存の imageUrl（`/{storeId}/{filename}` 形式、`?v=` 付きも可）から filename を取り出す。
// 同一メニューの画像差し替え時に同じ S3 キーへ上書きするために使う（ADR 0065）。
// 外部 URL や不正な形式の場合は null を返し、呼び出し側で新規採番する。
export function extractMenuImageFilename(imageUrl: string | null | undefined, storeId: string): string | null {
  if (!imageUrl || !imageUrl.startsWith(`/${storeId}/`)) return null;
  const filename = imageUrl.slice(`/${storeId}/`.length).split('?')[0];
  return filename && !filename.includes('/') ? filename : null;
}

// 画像のアップロード
// 同一メニューでは同じ S3 キー（= 画像 ID）へ上書きする（ADR 0065）:
// - currentImageUrl があればその filename を再利用（差し替えでもキー不変）
// - 無ければ（新規メニュー・初回アップロード）uuid を採番
// 上書きでもブラウザ/CDN キャッシュが更新されるよう、保存 URL に ?v={epoch} を付ける。
export const uploadMenuImage = createAsyncThunk<
  { menuId: string; imageUrl: string },
  { file: File; storeId: string; currentImageUrl?: string | null },
  { state: RootState }
>(
  'menu/uploadMenuImage',
  async ({ file, storeId, currentImageUrl }, { rejectWithValue }) => {
    try {
      const resized = await resizeImage(file);
      const filename = extractMenuImageFilename(currentImageUrl, storeId) ?? uuidv4();
      await uploadImageToS3(resized, storeId, filename);
      return { menuId: storeId, imageUrl: `/${storeId}/${filename}?v=${Date.now()}` };
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// メニューの並び順を更新
export const reorderMenus = createAsyncThunk<
  void,
  { storeId: string; updatedMenus: MenuItem[] }
>(
  'menu/reorderMenus',
  async ({ storeId, updatedMenus }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/menus/reorder`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ menus: updatedMenus }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// メニューの受注停止/再開を更新（BE ADR 0085 / FE ADR 0054）
export const updateMenuSuspension = createAsyncThunk<
  MenuItem,
  {
    storeId: string;
    menuId: string;
    suspended: boolean;
    suspendedUntil: string | null; // "YYYY-MM-DDTHH:mm"（datetime-local値）。null = 恒久停止
  }
>(
  'menu/updateMenuSuspension',
  async ({ storeId, menuId, suspended, suspendedUntil }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuItem>(`/stores/${storeId}/menus/${menuId}/suspension`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suspended, suspendedUntil }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// メニュー情報を更新
export const updateMenu = createAsyncThunk<
  MenuItem,
  { 
    storeId: string; 
    menuId: string; 
    name: string; 
    price: number; 
    imageUrl: string | null;
    menuOptionGroupId: string | null;
  }
>(
  'menu/updateMenu',
  async ({ storeId, menuId, name, price, imageUrl, menuOptionGroupId }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuItem>(`/stores/${storeId}/menus/${menuId}`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name, 
          price, 
          imageUrl, 
          menuOptionGroupId
        }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);